import { _, _any } from "i18n";
import { Calendar, MapPin, Pencil } from "lucide-react";
import { memo, useState } from "react";
import { Link } from "react-router";
import { useImageSrcWithFallback } from "../../hooks/useImageSrcWithFallback";
//@ts-ignore
import clubPlaceholder from "ui-kit/assets/images/logo_fixtures.png";
import SelectComponent from "../../../../ui-kit/src/SelectComponent/SelectComponent";
import { colorSelectValues, formatDate, getColorSelectValue } from "../functions";
import type { Colors, GPGameResponse, GPGameTeamResponse, GPPermissionsResponse, SelectColors } from "../types";
import { Shirt } from "./assets/Shirt";
import { Shorts } from "./assets/Shorts";

export type GamePageHeaderBackLink = {
	to: string;
	label: string;
};

interface GamePageHeaderProps {
	gameData: GPGameResponse;
	homeTeam: GPGameTeamResponse | null;
	awayTeam: GPGameTeamResponse | null;
	locale: string;
	permissions: GPPermissionsResponse;
	handleChangeColors: (id: number, colors: Colors) => void;
	handleChangeScore: (id: number, scores: { homeTeam?: number; awayTeam?: number }) => void;
	handleChangeDesc: (id: number, description: string | null) => void;
	backLink?: GamePageHeaderBackLink;
	editGameHref?: string;
	editGameLabel?: string;
	allowEditDescription?: boolean;
	showLocation?: boolean;
}

function getTeamSubtitle(gameTeam: GPGameTeamResponse | null): string {
	if (gameTeam === null) {
		return "-";
	}

	const category = gameTeam.team?.category ?? "";
	const teamName = gameTeam.team?.name || gameTeam.team?.category ? gameTeam.team?.name : gameTeam.teamName;

	return `${category} ${teamName ?? "-"}`.trim();
}

function GamePageHeaderInner({
	gameData,
	homeTeam,
	awayTeam,
	locale,
	permissions,
	handleChangeColors,
	handleChangeScore,
	handleChangeDesc,
	backLink,
	editGameHref,
	editGameLabel,
	allowEditDescription = false,
	showLocation = false,
}: GamePageHeaderProps) {
	const canEditScoreInline = permissions.editScore && editGameHref == null;
	const [edit, setEdit] = useState({
		homeTeam: false,
		awayTeam: false,
		score: false,
		gameTypeDesc: allowEditDescription && permissions.editScore && (gameData.description === null || gameData.description === ""),
	});
	const [score, setScore] = useState({ homeTeam: homeTeam?.score ?? undefined, awayTeam: awayTeam?.score ?? undefined });
	const [desc, setDesc] = useState(gameData.description);
	const [selectColors, setSelectColors] = useState<SelectColors>({
		homeTeam: {
			shirt: homeTeam?.colorShirt ?? undefined,
			shorts: homeTeam?.colorShort ?? undefined,
		},
		awayTeam: {
			shirt: awayTeam?.colorShirt ?? undefined,
			shorts: awayTeam?.colorShort ?? undefined,
		},
	});

	const getIconColor = (gameTeam: GPGameTeamResponse | null, colorType: "shirt" | "shorts"): string => {
		if (gameTeam === null) {
			return "transparent";
		}

		if (gameTeam.gameDressKit) {
			if (colorType === "shirt") {
				return gameTeam.gameDressKit.shirtColorPrimary || "transparent";
			}
			return gameTeam.gameDressKit.shortsColor || "transparent";
		}

		if (colorType === "shirt") {
			return gameTeam.colorShirt || "transparent";
		}
		return gameTeam.colorShort || "transparent";
	};

	const getColorName = (gameTeam: GPGameTeamResponse | null, colorType: "shirt" | "shorts"): string | null => {
		if (gameTeam === null) {
			return null;
		}

		const namedColor = colorType === "shirt" ? gameTeam.colorShirt : gameTeam.colorShort;
		if (namedColor == null) {
			return null;
		}

		return _any(`enum.colors.${namedColor.toLowerCase()}`);
	};

	const getSecondaryShirtColor = (gameTeam: GPGameTeamResponse | null): string | null => {
		const secondaryColor = gameTeam?.gameDressKit?.shirtColorSecondary;
		if (secondaryColor == null || secondaryColor === "") {
			return null;
		}
		return secondaryColor;
	};

	const onColorChange = (team: keyof SelectColors, type: string, value: { value: string | number; label: string } | null) => {
		setSelectColors({
			...selectColors,
			[team]: {
				...selectColors[team],
				[type]: value?.value,
			},
		});
	};

	const onScoreChange = (value: string, team: string) => {
		setScore({ ...score, [team]: Number(value) });
	};

	const handleSubmitColors = (team: string) => {
		if (team === "homeTeam" && homeTeam?.id) {
			handleChangeColors(homeTeam.id, selectColors.homeTeam);
		}
		if (team === "awayTeam" && awayTeam?.id) {
			handleChangeColors(awayTeam.id, selectColors.awayTeam);
		}
		setEdit({ ...edit, [team]: false });
	};

	const handleSubmitScore = () => {
		handleChangeScore(gameData?.id ?? 0, score);
		setEdit({ ...edit, score: false });
	};

	const handleCancelColors = (team: string) => {
		setSelectColors({
			...selectColors,
			[team]: {
				shirt: team === "homeTeam" ? homeTeam?.colorShirt ?? undefined : awayTeam?.colorShirt ?? undefined,
				shorts: team === "homeTeam" ? homeTeam?.colorShort ?? undefined : awayTeam?.colorShort ?? undefined,
			},
		});
		setEdit({ ...edit, [team]: false });
	};

	const handleCancelScore = () => {
		setScore({ homeTeam: homeTeam?.score ?? undefined, awayTeam: awayTeam?.score ?? undefined });
		setEdit({ ...edit, score: false });
	};

	const onDescChange = (value: string) => {
		setDesc(value);
	};

	const handleSubmitDesc = () => {
		handleChangeDesc(gameData?.id ?? 0, desc ?? "");
		setEdit({ ...edit, gameTypeDesc: false });
	};

	const handleCancelDesc = () => {
		setDesc(gameData.description ? gameData.description : null);
		setEdit({ ...edit, gameTypeDesc: false });
	};

	const { imgSrc: homeClubLogoSrc, onImgError: onHomeClubLogoError } = useImageSrcWithFallback({
		sourceKey: homeTeam?.club?.logo ?? null,
		fallbackSrc: clubPlaceholder,
	});
	const { imgSrc: awayClubLogoSrc, onImgError: onAwayClubLogoError } = useImageSrcWithFallback({
		sourceKey: awayTeam?.club?.logo ?? null,
		fallbackSrc: clubPlaceholder,
	});

	const locationText = showLocation && gameData.location != null && gameData.location !== "" ? gameData.location : null;
	const secondaryMeta = locationText ?? (showLocation ? null : desc);
	const showActionsRow = backLink != null || editGameHref != null;

	const renderKitSection = (teamKey: "homeTeam" | "awayTeam", gameTeam: GPGameTeamResponse | null) => {
		const isEditing = edit[teamKey];

		if (isEditing) {
			return (
				<div className="select">
					<div className="colorSelects">
						<div className="selectContainer">
							<div className="selectLabel">{_("app.coach.match.shirt_title")}</div>
							<SelectComponent
								value={getColorSelectValue(selectColors[teamKey]?.shirt)}
								handleChange={(value) => onColorChange(teamKey, "shirt", value)}
								items={colorSelectValues(_ as (key: string) => string)}
							/>
						</div>
						<div className="selectContainer">
							<div className="selectLabel">{_("app.coach.match.shorts_title")}</div>
							<SelectComponent
								value={getColorSelectValue(selectColors[teamKey]?.shorts)}
								handleChange={(value) => onColorChange(teamKey, "shorts", value)}
								items={colorSelectValues(_ as (key: string) => string)}
							/>
						</div>
					</div>
					<div className="confirmCancel">
						<span className="icon-new icon-checkmark icon-12" onClick={() => handleSubmitColors(teamKey)} />
						<span className="icon-new icon-cross icon-12" onClick={() => handleCancelColors(teamKey)} />
					</div>
				</div>
			);
		}

		const shirtName = getColorName(gameTeam, "shirt");
		const shortsName = getColorName(gameTeam, "shorts");
		const secondaryShirt = getSecondaryShirtColor(gameTeam);

		return (
			<div className="jersey">
				<div className="colorShirt" style={{ color: getIconColor(gameTeam, "shirt") }}>
					<Shirt />
				</div>
				{shirtName != null && <div className="colorName">{shirtName}</div>}
				{secondaryShirt != null && (
					<>
						<div className="colorShirt" style={{ color: secondaryShirt }}>
							<Shirt />
						</div>
						{secondaryShirt.startsWith("#") === false && <div className="colorName">{secondaryShirt}</div>}
					</>
				)}
				<div className="colorShorts" style={{ color: getIconColor(gameTeam, "shorts") }}>
					<Shorts />
				</div>
				{shortsName != null && <div className="colorName">{shortsName}</div>}
				{gameTeam?.permissions?.editColors && (
					<span onClick={() => setEdit({ ...edit, [teamKey]: true })} className="icon-new icon-crayon icon-12" />
				)}
			</div>
		);
	};

	const renderTeamLogo = (gameTeam: GPGameTeamResponse | null, imgSrc: string | null, onImgError: () => void) => {
		const image = <img src={imgSrc ?? clubPlaceholder} alt="" onError={onImgError} />;

		if (gameTeam?.url) {
			return (
				<div className="gamePageHeader-logo">
					<Link target={"_blank"} to={gameTeam.url}>
						{image}
					</Link>
				</div>
			);
		}

		return <div className="gamePageHeader-logo">{image}</div>;
	};

	return (
		<div className="gamePageHeader-container">
			{showActionsRow && (
				<div className="gamePageHeader-actions">
					<div className="gamePageHeader-actionsLeft">
						{backLink != null && (
							<Link to={backLink.to} className="gamePageHeader-backLink">
								<span className="icon-new icon-arrow-left icon-12" />
								{backLink.label}
							</Link>
						)}
					</div>
					<div className="gamePageHeader-actionsRight">
						{editGameHref != null && (
							<Link to={editGameHref} className="gamePageHeader-editGame">
								<Pencil size={14} />
								{editGameLabel ?? _("discover.games.tooltip.editGame")}
							</Link>
						)}
					</div>
				</div>
			)}

			<div className="gamePageHeader-main">
				<div className="gamePageHeader-team left">
					<div className="gamePageHeader-teamInfo">
						<div className="gamePageHeader-sideBadge">{_("gamePage.header.home")}</div>
						<div className="name">{homeTeam?.clubName ?? _("gamePage.header.noClub")}</div>
						<div className="category">{getTeamSubtitle(homeTeam)}</div>
						<div className="bottom">{renderKitSection("homeTeam", homeTeam)}</div>
					</div>
					{renderTeamLogo(homeTeam, homeClubLogoSrc, onHomeClubLogoError)}
				</div>

				<div className="gamePageHeader-gameInfo">
					<div className="gameInfo-top">
						{edit.score ? (
							<>
								<div className="score">
									<input
										className="scoreInput"
										type="number"
										value={score.homeTeam}
										onChange={(e) => onScoreChange(e.target.value, "homeTeam")}
									/>
									<span className="scoreSeparator">-</span>
									<input
										className="scoreInput"
										type="number"
										value={score.awayTeam}
										onChange={(e) => onScoreChange(e.target.value, "awayTeam")}
									/>
								</div>
								<div className="confirmCancel">
									<span className="icon-new icon-checkmark icon-12" onClick={() => handleSubmitScore()} />
									<span className="icon-new icon-cross icon-12" onClick={() => handleCancelScore()} />
								</div>
							</>
						) : (
							<>
								<div className="score">
									<span className="scoreHome">{homeTeam?.score ?? "-"}</span>
									<span className="scoreSeparator">:</span>
									<span className="scoreAway">{awayTeam?.score ?? "-"}</span>
								</div>
								{canEditScoreInline && (
									<span onClick={() => setEdit({ ...edit, score: true })} className="icon-new icon-crayon icon-12" />
								)}
							</>
						)}
					</div>
				</div>

				<div className="gamePageHeader-team">
					{renderTeamLogo(awayTeam, awayClubLogoSrc, onAwayClubLogoError)}
					<div className="gamePageHeader-teamInfo">
						<div className="gamePageHeader-sideBadge">{_("gamePage.header.away")}</div>
						<div className="name">{awayTeam?.clubName ?? _("gamePage.header.noClub")}</div>
						<div className="category">{getTeamSubtitle(awayTeam)}</div>
						<div className="bottom">{renderKitSection("awayTeam", awayTeam)}</div>
					</div>
				</div>

				<div className="gameInfo-meta">
					<div className="metaItem">
						<Calendar size={14} />
						<span>{gameData.date ? formatDate(gameData.date, locale) : "-"}</span>
					</div>
					{allowEditDescription && edit.gameTypeDesc ? (
						<>
							<input
								className="descInput"
								placeholder={_("gamePage.label.gameTypeInfo")}
								type="text"
								value={desc ?? ""}
								maxLength={32}
								onChange={(e) => onDescChange(e.target.value)}
							/>
							<div className="confirmCancel">
								<span className="icon-new icon-checkmark icon-12" onClick={() => handleSubmitDesc()} />
								<span className="icon-new icon-cross icon-12" onClick={() => handleCancelDesc()} />
							</div>
						</>
					) : (
						secondaryMeta != null &&
						secondaryMeta !== "" && (
							<div className="metaItem">
								{locationText != null ? <MapPin size={14} /> : null}
								<span>{secondaryMeta}</span>
								{allowEditDescription && permissions.editScore && (
									<span onClick={() => setEdit({ ...edit, gameTypeDesc: true })} className="icon-new icon-crayon icon-12" />
								)}
							</div>
						)
					)}
					{allowEditDescription &&
						permissions.editScore &&
						edit.gameTypeDesc === false &&
						(secondaryMeta == null || secondaryMeta === "") && (
							<div className="metaItem">
								<span onClick={() => setEdit({ ...edit, gameTypeDesc: true })} className="icon-new icon-crayon icon-12" />
							</div>
						)}
				</div>
			</div>
		</div>
	);
}

function areHeaderPropsEqual(prev: GamePageHeaderProps, next: GamePageHeaderProps): boolean {
	return (
		prev.gameData === next.gameData &&
		prev.homeTeam === next.homeTeam &&
		prev.awayTeam === next.awayTeam &&
		prev.locale === next.locale &&
		prev.permissions === next.permissions &&
		prev.backLink?.to === next.backLink?.to &&
		prev.backLink?.label === next.backLink?.label &&
		prev.editGameHref === next.editGameHref &&
		prev.editGameLabel === next.editGameLabel &&
		prev.allowEditDescription === next.allowEditDescription &&
		prev.showLocation === next.showLocation
	);
}

export const GamePageHeader = memo(GamePageHeaderInner, areHeaderPropsEqual);
